/*
 * Copyright (c) 2016-2023 VMware, Inc. All Rights Reserved.
 * This software is released under MIT license.
 * The full license information can be found in LICENSE in the root directory of this project.
 */
import { onChildListMutation, onFirstInteraction } from '../utils/events.js';
import { getFlattenedDOMTree, getFlattenedFocusableItems } from '../utils/traversal.js';
import { contextMenuClick } from '../utils/dom.js';
import { validKeyNavigationCode } from '../utils/keycodes.js';
import { focusElement, getActiveElement, initializeKeyListItems, setActiveKeyListItem, simpleFocusable, } from '../utils/focus.js';
import { getNextKeyGridItem } from './key-navigation.utils.js';
/**
 * Given a 2d array grid structure provide keyboard navigation following aria grid spec
 * https://w3c.github.io/aria-practices/#gridNav_focus
 */
export function keyNavigationGrid() {
    return (target) => target.addInitializer((instance) => new KeyNavigationGridController(instance));
}
export class KeyNavigationGridController {
    constructor(host) {
        this.host = host;
        this.observers = [];
        this.host.addController(this);
    }
    get hostGrid() {
        return this.host.keyNavGrid ? this.host.keyNavGrid : this.host;
    }
    get hostRows() {
        const rows = Array.from(this.host.rows);
        if (this.host.columnRow) {
            rows.unshift(this.host.columnRow);
        }
        return rows;
    }
    get hostCells() {
        const cells = Array.from(this.host.cells);
        if (this.host.columns) {
            cells.unshift(...Array.from(this.host.columns));
        }
        return cells;
    }
    get activeCell() {
        return Array.from(this.hostCells).find(i => i.tabIndex === 0);
    }
    async hostConnected() {
        await this.host.updateComplete;
        onFirstInteraction(this.host).then(() => {
            initializeKeyListItems(this.hostCells);
            this.hostGrid.addEventListener('mouseup', (e) => this.clickCell(e));
            this.hostGrid.addEventListener('keydown', (e) => this.keynavCell(e));
            this.hostGrid.addEventListener('keyup', (e) => this.updateCellActivation(e));
        });
        this.observers.push(onChildListMutation(this.host, () => initializeKeyListItems(this.hostCells)));
    }
    hostDisconnected() {
        this.observers.forEach(o => o?.disconnect());
    }
    clickCell(e) {
        if (!contextMenuClick(e)) {
            const activeCell = e.composedPath().find(i => this.hostCells.find(c => c === i));
            if (activeCell) {
                this.setActiveCell(e, activeCell);
            }
        }
    }
    keynavCell(e) {
        if (validKeyNavigationCode(e) && simpleFocusable(getActiveElement())) {
            const { x, y } = getNextKeyGridItem(this.hostCells, this.hostRows, {
                code: e.code,
                ctrlKey: e.ctrlKey,
                dir: this.host.dir,
            });
            const nextCell = Array.from(getFlattenedDOMTree(this.hostRows[y])).filter(c => !!this.hostCells.find(i => i === c))[x];
            this.setActiveCell(e, nextCell);
            e.preventDefault();
        }
    }
    setActiveCell(e, activeCell) {
        setActiveKeyListItem(this.hostCells, activeCell);
        // https://w3c.github.io/aria-practices/#gridNav_focus
        const items = getFlattenedFocusableItems(activeCell);
        const simpleItems = items.filter(i => simpleFocusable(i));
        if (simpleItems.length === 1 && items.length === 1) {
            focusElement(simpleItems[0]);
        }
        else {
            focusElement(activeCell);
        }
        activeCell.dispatchEvent(new CustomEvent('cdsKeyChange', {
            bubbles: true,
            detail: { code: e.code, shiftKey: e.shiftKey, activeItem: activeCell },
        }));
    }
    updateCellActivation(e) {
        if (e.code === 'Escape') {
            this.activeCell?.focus();
        }
        if (e.code === 'Enter' && this.activeCell === e.composedPath()[0]) {
            getFlattenedFocusableItems(this.activeCell)[0]?.focus();
        }
    }
}
